import React, { useState, useEffect } from "react";
import Content from "../../Components/Core/Content";
import { ViewAction } from "../../Enums/ViewActionEnum";
import FormInput from "../../Components/Core/FormInput";
import {
  CompleteProfile,
  EditProfile,
  GetProfileById,
  setPassword as saveUserPassword,
} from "../../Services/AuthService";
import { Button, CircularProgress } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import Notification from "../../Components/Core/Notification";
import Loader from "../../Components/Core/Loader";
import { getDecodedUserId } from "../../Services/GeneralService";

const PageShell = styled("div")({
  background: "linear-gradient(135deg, #e5eff3ff 0%, #ffffff 100%)",
  minHeight: "100vh",
  padding: "40px 20px",
  position: "relative",
  fontFamily: '"MyPersianFont", sans-serif',
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    background:
      "radial-gradient(ellipse at center, rgba(124, 200, 144, 0.08) 0%, transparent 70%)",
    pointerEvents: "none",
  },
  "@media(max-width: 768px)": {
    padding: "24px 0",
  },
});

const ProfileCard = styled("div")({
  background:
    "linear-gradient(145deg, #ffffff 0%, rgba(248, 255, 254, 0.95) 100%)",
  borderRadius: "32px",
  boxShadow:
    "0 32px 80px rgba(5, 50, 81, 0.12), 0 16px 40px rgba(5, 50, 81, 0.08)",
  border: "1px solid rgba(124, 200, 144, 0.15)",
  padding: "48px 40px",
  position: "relative",
  overflow: "hidden",
  maxWidth: "760px",
  margin: "0 auto",
  fontFamily: '"MyPersianFont", sans-serif',
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "8px",
    background: "linear-gradient(90deg, #053251 0%, #7cc890 100%)",
    borderRadius: "32px 32px 0 0",
  },
  "@media (max-width: 768px)": {
    padding: "32px 20px",
    borderRadius: "24px",
    margin: "0 16px",
  },
});

const PageTitle = styled("h2")({
  fontSize: "32px",
  fontWeight: 700,
  background: "linear-gradient(135deg, #053251 0%, #2a5a72 100%)",
  backgroundClip: "text",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  letterSpacing: "-1px",
  textAlign: "center",
  marginBottom: "12px",
  fontFamily: '"MyPersianFont", sans-serif',
  "@media (max-width: 768px)": {
    fontSize: "24px",
  },
});

const PageSubtitle = styled("p")({
  textAlign: "center",
  color: "#2a5a72",
  fontSize: "14px",
  lineHeight: 1.8,
  marginBottom: "36px",
  opacity: 0.85,
  fontFamily: '"MyPersianFont", sans-serif',
});

const FieldsGrid = styled("div")({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: "8px 20px",
  "@media (max-width: 768px)": {
    gridTemplateColumns: "1fr",
  },
});

const FullWidthField = styled("div")({
  gridColumn: "1 / -1",
});

const formatMobileNumber = (mobile) => {
  if (!mobile) return "";
  const digits = String(mobile).replace(/\D/g, "");
  if (digits.length === 10 && !digits.startsWith("0")) {
    return `0${digits}`;
  }
  return mobile;
};

const SecurityBox = styled("div")({
  marginTop: "12px",
  marginBottom: "8px",
  padding: "24px 20px 8px",
  borderRadius: "20px",
  background: "rgba(5, 50, 81, 0.03)",
  border: "1px solid rgba(5, 50, 81, 0.08)",
});

const SectionLabel = styled("h4")({
  fontSize: "16px",
  fontWeight: 700,
  color: "#053251",
  marginBottom: "6px",
  fontFamily: '"MyPersianFont", sans-serif',
});

const HintText = styled("p")({
  color: "#2a5a72",
  fontSize: "13px",
  lineHeight: 1.7,
  marginBottom: "16px",
  opacity: 0.8,
  fontFamily: '"MyPersianFont", sans-serif',
});

const StyledButton = styled(Button)(({ disabled: isDisabled }) => ({
  borderRadius: "16px",
  padding: "14px 40px",
  fontSize: "16px",
  fontWeight: 600,
  textTransform: "none",
  minWidth: "180px",
  fontFamily: '"MyPersianFont", sans-serif',
  background: isDisabled
    ? "linear-gradient(135deg, rgba(5, 50, 81, 0.3) 0%, rgba(5, 50, 81, 0.2) 100%)"
    : "linear-gradient(135deg, #053251 0%, #2a5a72 100%)",
  color: "white",
  boxShadow: "none",
  "&:hover": {
    background: isDisabled
      ? "linear-gradient(135deg, rgba(5, 50, 81, 0.3) 0%, rgba(5, 50, 81, 0.2) 100%)"
      : "linear-gradient(135deg, #2a5a72 0%, #1e4159 100%)",
    boxShadow: isDisabled ? "none" : "0 12px 24px rgba(5, 50, 81, 0.25)",
  },
  "@media (max-width: 768px)": {
    width: "100%",
    minWidth: "unset",
  },
}));

const ButtonRow = styled("div")({
  display: "flex",
  justifyContent: "center",
  marginTop: "28px",
});

const inputClasses = "py-2 px-3";

const Profile = ({ action }) => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [nationalCode, setNationalCode] = useState("");
  const [email, setEmail] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [hasPassword, setHasPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [userId, setUserId] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const from = location.state?.from || "/";
  const isCreate = action === ViewAction.CREATE;
  const title = action === ViewAction.EDIT ? "ویرایش پروفایل" : "تکمیل پروفایل";

  const handleSubmit = async () => {
    try {
      setLoading(true);
      if (isCreate) {
        if (password && password !== confirmPassword) {
          throw new Error("رمز عبور و تکرار آن یکسان نیستند");
        }
        if (password && password.length < 8) {
          throw new Error("رمز عبور باید حداقل ۸ کاراکتر باشد");
        }
        await CompleteProfile(
          firstName,
          lastName,
          nationalCode,
          email,
          password || undefined,
        );
      } else if (
        action === ViewAction.EDIT ||
        action === ViewAction.AdminEdit
      ) {
        await EditProfile(userId, firstName, lastName, nationalCode, email);
      }
      setSnackbarMessage("با موفقیت ثبت شد!");
      setSnackbarSeverity("success");
      navigate(from);
    } catch (error) {
      console.error("Error completing profile:", error);
      setSnackbarMessage(error.message);
      setSnackbarSeverity("error");
    } finally {
      setLoading(false);
      setOpenSnackbar(true);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);

      let currentUserId;
      if (action === ViewAction.AdminEdit) {
        currentUserId = id;
      } else {
        currentUserId = getDecodedUserId();
      }

      const profileData = await GetProfileById(currentUserId);
      setUserId(currentUserId);
      setFirstName(profileData.firstName);
      setLastName(profileData.lastName);
      setNationalCode(profileData.nationalCode);
      setEmail(profileData.email);
      setMobileNumber(formatMobileNumber(profileData.mobileNumber));
      setHasPassword(!!profileData.hasPassword);
    } catch (error) {
      console.error("Error fetching profile data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = title;
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action]);

  const handleSetPassword = async () => {
    try {
      if (password.length < 8) {
        throw new Error("رمز عبور باید حداقل ۸ کاراکتر باشد");
      }
      if (password !== confirmPassword) {
        throw new Error("رمز عبور و تکرار آن یکسان نیستند");
      }
      if (hasPassword && !currentPassword) {
        throw new Error("لطفا رمز عبور فعلی را وارد کنید");
      }

      setPasswordLoading(true);
      await saveUserPassword(password, currentPassword || undefined);
      setHasPassword(true);
      setCurrentPassword("");
      setPassword("");
      setConfirmPassword("");
      setSnackbarMessage("رمز عبور با موفقیت ثبت شد!");
      setSnackbarSeverity("success");
    } catch (error) {
      setSnackbarMessage(error.message);
      setSnackbarSeverity("error");
    } finally {
      setPasswordLoading(false);
      setOpenSnackbar(true);
    }
  };

  const formFields = (
    <>
      <FieldsGrid>
        <FullWidthField>
          <FormInput
            name={"شماره موبایل"}
            value={mobileNumber}
            for={"mobileNumber"}
            dir="ltr"
            disabled={true}
            styleClasses={`${inputClasses} bg-light`}
            onChange={() => {}}
          />
        </FullWidthField>
        <FormInput
          name={"نام"}
          onChange={(e) => setFirstName(e.target.value)}
          value={firstName}
          for={"firstName"}
          required={true}
          styleClasses={inputClasses}
        />
        <FormInput
          name={"نام خانوادگی"}
          onChange={(e) => setLastName(e.target.value)}
          value={lastName}
          for={"lastName"}
          required={true}
          styleClasses={inputClasses}
        />
        <FormInput
          name={"کد ملی"}
          onChange={(e, value) => setNationalCode(value)}
          value={nationalCode}
          for={"nationalCode"}
          required={true}
          numericOnly={true}
          styleClasses={inputClasses}
        />
        <FormInput
          name={"ایمیل"}
          onChange={(e) => setEmail(e.target.value)}
          value={email}
          for={"email"}
          dir={"ltr"}
          styleClasses={inputClasses}
        />
      </FieldsGrid>

      {isCreate && (
        <SecurityBox>
          <SectionLabel>
            <span>رمز عبور</span>
          </SectionLabel>
          <HintText>
            اختیاری است. با تنظیم رمز می‌توانید بدون کد پیامک وارد شوید.
          </HintText>
          <FieldsGrid>
            <FormInput
              name={"رمز عبور"}
              type="password"
              onChange={(e, value) => setPassword(value)}
              value={password}
              for={"password"}
              dir={"ltr"}
              autoComplete="new-password"
              styleClasses={inputClasses}
            />
            <FormInput
              name={"تکرار رمز عبور"}
              type="password"
              onChange={(e, value) => setConfirmPassword(value)}
              value={confirmPassword}
              for={"confirmPassword"}
              dir={"ltr"}
              autoComplete="new-password"
              styleClasses={inputClasses}
            />
          </FieldsGrid>
        </SecurityBox>
      )}

      <ButtonRow>
        <StyledButton
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            <span>ثبت اطلاعات</span>
          )}
        </StyledButton>
      </ButtonRow>

      {action === ViewAction.EDIT && (
        <SecurityBox style={{ marginTop: 36 }}>
          <SectionLabel>
            <span>{hasPassword ? "تغییر رمز عبور" : "تنظیم رمز عبور"}</span>
          </SectionLabel>
          <HintText>
            با تنظیم رمز عبور می‌توانید بدون کد پیامک وارد شوید.
          </HintText>
          {hasPassword && (
            <FormInput
              name={"رمز عبور فعلی"}
              type="password"
              onChange={(e, value) => setCurrentPassword(value)}
              value={currentPassword}
              for={"currentPassword"}
              dir={"ltr"}
              autoComplete="current-password"
              styleClasses={inputClasses}
            />
          )}
          <FieldsGrid>
            <FormInput
              name={"رمز عبور جدید"}
              type="password"
              onChange={(e, value) => setPassword(value)}
              value={password}
              for={"newPassword"}
              dir={"ltr"}
              autoComplete="new-password"
              styleClasses={inputClasses}
            />
            <FormInput
              name={"تکرار رمز عبور جدید"}
              type="password"
              onChange={(e, value) => setConfirmPassword(value)}
              value={confirmPassword}
              for={"confirmNewPassword"}
              dir={"ltr"}
              autoComplete="new-password"
              styleClasses={inputClasses}
            />
          </FieldsGrid>
          <ButtonRow>
            <StyledButton
              variant="contained"
              onClick={handleSetPassword}
              disabled={passwordLoading || !password}
            >
              {passwordLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                <span>{hasPassword ? "تغییر رمز عبور" : "ثبت رمز عبور"}</span>
              )}
            </StyledButton>
          </ButtonRow>
        </SecurityBox>
      )}
    </>
  );

  const notification = (
    <Notification
      openSnackbar={openSnackbar}
      snackbarMessage={snackbarMessage}
      snackbarSeverity={snackbarSeverity}
      setOpenSnackbar={setOpenSnackbar}
    />
  );

  if (loading) {
    return <Loader />;
  }

  if (isCreate) {
    return (
      <>
        <PageShell>
          <Content>
            <ProfileCard>
              <PageTitle>
                <span>{title}</span>
              </PageTitle>
              <PageSubtitle>
                برای استفاده از خدمات، اطلاعات حساب خود را تکمیل کنید.
              </PageSubtitle>
              {formFields}
            </ProfileCard>
          </Content>
        </PageShell>
        {notification}
      </>
    );
  }

  return (
    <>
      <Content>
        <div className="container mb-5">
          <h2 className="mb-4 row mt-5 highlight-box fw-bold color-blue highlight-secondary">
            <span>{title}</span>
          </h2>
          {formFields}
        </div>
        {notification}
      </Content>
    </>
  );
};

export default Profile;
